import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, GraduationCap, LogOut, BookOpenCheck, Gamepad2, ShieldCheck, Award } from "lucide-react";
import { academy, getAcademyToken, setAcademyToken, DISCLAIMER } from "@/lib/academy";
import { AcademyFrame } from "@/components/academy/AcademyFrame";
import { AcademyLogin } from "@/components/academy/AcademyLogin";
import { AcademyDashboard } from "@/components/academy/AcademyDashboard";

const FEATURES = [
  [BookOpenCheck, "8 short modules", "From spotting illegal content to escalating a rejected report under the DSA."],
  [Gamepad2, "Real platforms", "Step-by-step playbooks for Roblox, Discord, Facebook, adult sites and more."],
  [ShieldCheck, "Safe and practical", "Missions, quizzes with explanations, five case files and a live Ops Desk simulation."],
  [Award, "Certificate", "A downloadable Certificate of Completion with a verification page."],
];

function Landing() {
  return (
    <div className="grid items-start gap-10 lg:grid-cols-[1.2fr_1fr]" data-testid="academy-landing">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-3 py-1 text-[12px] font-semibold uppercase tracking-wider text-blue-300"><GraduationCap size={14} /> Reporter Academy</div>
        <h1 className="mt-4 text-4xl font-bold tracking-tight text-zinc-50 sm:text-5xl lg:text-6xl">Report it right.</h1>
        <p className="mt-4 max-w-xl text-[16px] leading-relaxed text-zinc-400">A free, plain-language course for everyday people who want to report harmful or illegal content online, and make their reports count. About 4–5 hours in short sessions.</p>
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {FEATURES.map(([Icon, t, d]) => (
            <div key={t} className="rounded-xl border border-zinc-800/70 bg-[#111113] p-4">
              <Icon size={18} className="text-[#FFCC00]" />
              <div className="mt-2 font-semibold text-zinc-100">{t}</div>
              <div className="mt-1 text-[13px] text-zinc-400">{d}</div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-[12px] leading-relaxed text-zinc-600">{DISCLAIMER}</p>
      </div>
      <AcademyLogin />
    </div>
  );
}

export function AcademyEnter() {
  const { token } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    setAcademyToken(token);
    navigate("/academy", { replace: true });
  }, [token, navigate]);
  return null;
}

export default function AcademyPage() {
  const [data, setData] = useState(null);
  const [state, setState] = useState(getAcademyToken() ? "loading" : "out");
  const load = useCallback(() => academy.get("/me").then((r) => { setData(r.data); setState("in"); }).catch(() => {
    setAcademyToken(null);
    setState("out");
  }), []);
  useEffect(() => { if (getAcademyToken()) load(); }, [load]);
  const logout = () => { setAcademyToken(null); setData(null); setState("out"); };

  return (
    <AcademyFrame>
      {state === "loading" && <div className="grid min-h-[50vh] place-items-center"><Loader2 className="animate-spin text-zinc-500" /></div>}
      {state === "out" && <Landing />}
      {state === "in" && data && (
        <>
          <div className="mb-4 flex items-center justify-end gap-3 text-[13px] text-zinc-500">
            <span data-testid="academy-user-email">{data.user.email}</span>
            <button data-testid="academy-logout" onClick={logout} className="inline-flex items-center gap-1 rounded-md border border-zinc-800 px-2.5 py-1 text-zinc-400 hover:text-zinc-100"><LogOut size={13} /> Sign out on this device</button>
          </div>
          <AcademyDashboard data={data} reload={load} />
        </>
      )}
    </AcademyFrame>
  );
}
